/**
 * The collection screen's data (SPEC Section 7 screen 8; Section 8 "Words I know").
 * localStorage only. Purely for fun, so every read tolerates a missing or broken store.
 */

const KEY = 'ab.collection.v1'

// The calls block (D-135): how many times the kid called a winner before the
// fight, how many were right, and the pairs where the call was wrong (keyed the
// way the story cache keys a pair, `a__b` with the slugs sorted). Counts only —
// the collection shows "23 right calls", never a share.
const emptyCalls = () => ({ made: 0, right: 0, wrong: {} })

// The surprise set (D-136): every pair whose fight was a surprise, keyed like
// the calls, with why. Also a count that only goes up.
const empty = () => ({ animals: {}, words: {}, calls: emptyCalls(), surprises: {} })

function read() {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (!c || typeof c !== 'object') return empty()
    // A store written before the calls block existed has no `calls`; it gets
    // the empty block and keeps everything else, never a wipe. Same for `surprises`.
    const calls = c.calls && typeof c.calls === 'object' ? { ...emptyCalls(), ...c.calls, wrong: c.calls.wrong && typeof c.calls.wrong === 'object' ? c.calls.wrong : {} } : emptyCalls()
    const surprises = c.surprises && typeof c.surprises === 'object' ? c.surprises : {}
    return { animals: c.animals || {}, words: c.words || {}, calls, surprises }
  } catch {
    return empty()
  }
}

/** The key a pair is stored under: order-independent, like the story cache. */
export const pairKey = (a, b) => [a, b].sort().join('__')

/**
 * Why a finished battle counts as a surprise (D-136) — an empty list when it
 * does not. An upset is any fight the kid called wrong; beyond that, the
 * objectively surprising pairs are the ones the venom rule decided, the ones
 * the winner was ten times lighter in, and the ones inside the close band —
 * about 1,600 of the roster's pairs. "The lighter animal won" and "decided on
 * weight" are deliberately not surprises: most of those are the out-of-domain
 * factor doing its job, and no kid reads a stingray beating a lion in water as
 * an upset. `numbers` is the decision the server sent with the story (D-085);
 * nothing here is computed from the roster.
 */
export function surpriseReasons({ numbers, winnerSlug, call = null }) {
  const why = []
  if (call && call !== winnerSlug) why.push('call')
  if (numbers && numbers.a && numbers.b) {
    const w = numbers.winner === 'a' ? numbers.a : numbers.b
    const l = numbers.winner === 'a' ? numbers.b : numbers.a
    if ((numbers.decisive || []).some((d) => d.rule === 'venom')) why.push('venom')
    if (w.weight_kg > 0 && l.weight_kg >= w.weight_kg * 10) why.push('light')
    if (numbers.close) why.push('close')
  }
  return why
}

function write(c) {
  try {
    localStorage.setItem(KEY, JSON.stringify(c))
  } catch {
    // ignore
  }
}

export function getCollection() {
  return read()
}

/** The slugs this device has taken into a battle, for the roster's "not yet met" marks. */
export function metAnimals() {
  return new Set(Object.keys(read().animals))
}

/**
 * How much of each roster group the kid has met — `{ category, label, met,
 * total }` per group, in roster order, a group nobody has touched included at
 * zero. Counts only, never a share: "9 of 14 dinosaurs" is a set to finish,
 * and a number that can only go up is the point.
 */
export function categoryProgress(groups, c = read()) {
  return groups.map((g) => ({
    category: g.category,
    label: g.label,
    met: g.slugs.filter((slug) => c.animals[slug]).length,
    total: g.slugs.length,
  }))
}

/**
 * Record a finished battle: both animals used, the winner credited, and — when
 * the kid called a winner on the matchup (D-135) — the call scored against the
 * result. `call` is the slug called, or null when no call was made.
 */
export function recordBattle({ a, b, winnerSlug, vocab, call = null, numbers = null }) {
  const c = read()
  for (const slug of [a, b]) {
    const e = c.animals[slug] || { battles: 0, wins: 0 }
    e.battles += 1
    if (slug === winnerSlug) e.wins += 1
    c.animals[slug] = e
  }
  if (vocab?.word) c.words[vocab.word.toLowerCase()] = { word: vocab.word, definition: vocab.definition, at: Date.now() }
  const made = call === a || call === b ? call : null
  if (made) {
    c.calls.made += 1
    if (made === winnerSlug) c.calls.right += 1
    else c.calls.wrong[pairKey(a, b)] = { called: made, winner: winnerSlug, at: Date.now() }
  }
  const why = surpriseReasons({ numbers, winnerSlug, call: made })
  if (why.length) c.surprises[pairKey(a, b)] = { why, winner: winnerSlug, at: Date.now() }
  write(c)
  return c
}

export function resetCollection() {
  write(empty())
}
